import * as React from 'react';
import { Box, Fade, Typography, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { PictureAsPdfOutlined } from '@mui/icons-material';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../../root-components/theme';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { LoadingSpinner } from '../../../presentation-components/display-elements-components/loading-spinner';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { IHotspotAnalysis, IHotspotItem } from '../../../../shared/services/analytics/hotspots.model';
import {
  analyticsEmissionTypeToggleFromDefaultMarketBased,
  type AnalyticsEmissionTypeToggle,
} from '../../../../shared/utils/default-market-based-utils';
import { getHotspotComparison } from '../../../../shared/utils/hotspot-utils';
import { downloadBlob } from '../../../../shared/utils/download-file-utils';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { EmissionBasisToggle } from '../../../presentation-components/analytics-components/emission-basis-toggle';
import { ComparisonPeriodSelect } from '../../../presentation-components/analytics-components/comparison-period-select';
import { DrillDownDrawer, IDrillSelection } from '../../../presentation-components/analytics-components/drill-down-drawer';
import { HotspotMetricDrawer, HotspotMetric } from '../../../presentation-components/analytics-components/hotspot-metric-drawer';
import { HotspotAnalysisBody } from '../../../presentation-components/analytics-components/hotspot-analysis-body';

// How many ranked items to fetch. Generous: the point is that the long tail is reachable at all.
// The PDF print page fetches the same, so its figures match the screen.
export const HOTSPOT_ITEM_LIMIT = 500;

// Dimension keys the ?dim= query param may carry. The API decides which of these an organisation
// actually gets (data.matrixDimensions); a stale value falls back when the data arrives. The PDF
// print page validates its own ?dim= against the same list.
export const HOTSPOT_DIMENSIONS = ['category', 'categoryGroup', 'scope', 'group', 'location', 'supplier', 'subsidiary', 'project'];

export const AnalyticsHotspotsPage = () => {
  const reportPeriodContext = React.useContext(ReportPeriodContext);
  const userDetailsContext = React.useContext(UserDetailsContext);
  const screenLabelsContext = React.useContext(ScreenLabelsContext);
  const userDetails = userDetailsContext.userDetails;
  const organisationDefaultMarketBased = userDetails?.organisation?.defaultMarketBased;
  const userNotificationsContext = React.useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const analyticsService = React.useMemo(() => new AnalyticsService(), []);
  const [searchParams, setSearchParams] = useSearchParams();

  // The report period comes from the global selector in the top bar; this page no longer carries
  // one of its own, so the two can never disagree.
  const allReportPeriods = reportPeriodContext.allReportPeriods;
  const selectedReportPeriodId = reportPeriodContext.lastSelectedReportId || '';

  // Seed dimension and basis from the URL synchronously so the first render never has a state/URL
  // mismatch. If the serialiser's mount run fires from a state snapshot that disagrees with the
  // URL, it rewrites the URL from stale defaults and the hydrate and serialise effects then
  // correct each other forever ("Maximum update depth exceeded").
  const [selectedEmissionType, setSelectedEmissionType] = React.useState<AnalyticsEmissionTypeToggle>(() => {
    const basis = searchParams.get('basis');
    if (basis === 'market') return 'marketBased';
    if (basis === 'location') return 'locationBased';
    return analyticsEmissionTypeToggleFromDefaultMarketBased(organisationDefaultMarketBased);
  });
  const [data, setData] = React.useState<IHotspotAnalysis>();
  const [matrixDimension, setMatrixDimension] = React.useState<string>(() => {
    const dim = searchParams.get('dim');
    return dim && HOTSPOT_DIMENSIONS.includes(dim) ? dim : 'category';
  });
  // '' = automatic (the previous period, derived as it always was); otherwise a report period id.
  // Seeded from the URL synchronously for the same reason as dimension and basis above.
  const [compareReportPeriodId, setCompareReportPeriodId] = React.useState<string>(
    () => searchParams.get('compare') || '',
  );
  const [showAllRanked, setShowAllRanked] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const showSkeleton = useSkeletonDelay(isLoading, 250);

  const scopeTwoMethod: 'market' | 'location' = selectedEmissionType === 'marketBased' ? 'market' : 'location';

  // The organisation default basis applies only while the URL does not dictate one: a shared
  // link's ?basis= must survive the default arriving after mount.
  React.useEffect(() => {
    if (searchParams.get('basis')) return;
    setSelectedEmissionType(analyticsEmissionTypeToggleFromDefaultMarketBased(organisationDefaultMarketBased));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationDefaultMarketBased]);

  // Hydrate dimension and basis from the URL. Keyed on searchParams, not just mount, so in-page
  // navigation works. Each field is only written when it differs from current state, which is what
  // stops this and the serialiser below ping-ponging.
  React.useEffect(() => {
    const basis = searchParams.get('basis');
    if (basis === 'market' && selectedEmissionType !== 'marketBased') setSelectedEmissionType('marketBased');
    if (basis === 'location' && selectedEmissionType !== 'locationBased') setSelectedEmissionType('locationBased');
    const dim = searchParams.get('dim');
    if (dim && HOTSPOT_DIMENSIONS.includes(dim) && dim !== matrixDimension) setMatrixDimension(dim);
    // Unlike dim/basis, an absent compare param is meaningful (automatic), so hydration must
    // clear the state too: the back button from ?compare=<id> to a compare-less URL restores
    // automatic. Still written only when different, which keeps the no-ping-pong rule.
    const compare = searchParams.get('compare') || '';
    if (compare !== compareReportPeriodId) setCompareReportPeriodId(compare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Serialise back so any hotspot view is shareable. replace: true so toggles do not pile up
  // history entries. Safe on its mount run because the state was seeded from the URL above, so the
  // only mount-time write is canonicalisation. Gated on userDetails so a provisional basis is
  // never baked into the URL before the organisation default is known (the org-default effect
  // above would then wrongly treat it as dictated by the link).
  React.useEffect(() => {
    if (!userDetails) return;
    const next = new URLSearchParams();
    next.set('dim', matrixDimension);
    next.set('basis', selectedEmissionType === 'marketBased' ? 'market' : 'location');
    // Automatic comparison is the absence of the param, matching the analytics dashboard.
    if (compareReportPeriodId) next.set('compare', compareReportPeriodId);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixDimension, selectedEmissionType, compareReportPeriodId]);

  const selectedReportPeriod = allReportPeriods?.find((item) => item.id === selectedReportPeriodId);
  const autoComparisonReportPeriod = React.useMemo(() => {
    if (!allReportPeriods || !selectedReportPeriod) return undefined;
    return [...allReportPeriods]
      .filter((item) => item.id !== selectedReportPeriod.id)
      .sort((a, b) => Number(b.calendarYear || 0) - Number(a.calendarYear || 0))
      .find((item) => Number(item.calendarYear || 0) < Number(selectedReportPeriod.calendarYear || 0));
  }, [allReportPeriods, selectedReportPeriod]);
  // An explicit "Compare to" choice wins over the automatic previous-period derivation. An id
  // that no longer resolves (deleted period, or the global selector moved onto the chosen period)
  // falls back to automatic rather than comparing a period with itself.
  const comparisonReportPeriod = React.useMemo(() => {
    if (compareReportPeriodId && compareReportPeriodId !== selectedReportPeriodId) {
      const override = allReportPeriods?.find((item) => item.id === compareReportPeriodId);
      if (override) return override;
    }
    return autoComparisonReportPeriod;
  }, [allReportPeriods, compareReportPeriodId, selectedReportPeriodId, autoComparisonReportPeriod]);

  React.useEffect(() => {
    if (!selectedReportPeriodId) {
      setData(undefined);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage('');
    analyticsService
      .getHotspots(selectedReportPeriodId, {
        scopeTwoMethod,
        comparisonReportPeriodId: comparisonReportPeriod?.id,
        // The endpoint defaults to 12 items per dimension. That default was silently hiding the
        // answer to the most common question people brought to this page ("what are my emissions for
        // <a specific thing>"), because anything outside the top 12 simply was not returned. Ask for
        // the long tail and cap the VIEW instead: the ranked table below expands on request, and the
        // Pareto chart still plots only the leaders.
        limit: HOTSPOT_ITEM_LIMIT,
      })
      .then((response) => {
        if (cancelled) return;
        setData(response);
        if (response?.matrixDimensions?.length && !response.matrixDimensions.includes(matrixDimension)) {
          setMatrixDimension(response.defaultDimension || response.matrixDimensions[0]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.log(err);
        setErrorMessage('Failed to load hotspot analysis. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportPeriodId, scopeTwoMethod, comparisonReportPeriod?.id]);

  const dimensionLabel = (dimension: string): string => {
    switch (dimension) {
      case 'category': return 'Category';
      case 'categoryGroup': return 'Category group';
      case 'scope': return 'Scope';
      case 'group': return 'Group';
      case 'location': return screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' });
      case 'supplier': return screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' });
      case 'subsidiary': return screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' });
      case 'project': return screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' });
      default: return dimension;
    }
  };

  // The comparison label and prior-period total, read from the payload (which echoes the comparison
  // the API used) so the drawers, the page body and the PDF agree.
  const { prevTotal, prevLabel } = React.useMemo(() => getHotspotComparison(data), [data]);

  // Drill-down panel
  const [selectedHotspot, setSelectedHotspot] = React.useState<IDrillSelection | null>(null);
  const openDrill = (item?: IHotspotItem) => {
    if (!item || !item.id) return; // can't drill without an entity id
    setSelectedHotspot({ dimension: matrixDimension, id: item.id, label: item.label, item });
  };
  // Close the drawer when the dimension / period / comparison / basis changes (stale otherwise).
  React.useEffect(() => {
    setSelectedHotspot(null);
  }, [matrixDimension, selectedReportPeriodId, scopeTwoMethod, comparisonReportPeriod?.id]);

  // Collapse the ranked table again on any change of what it is ranking, so switching dimension
  // does not leave 300 rows expanded from the previous one.
  React.useEffect(() => {
    setShowAllRanked(false);
  }, [matrixDimension, selectedReportPeriodId, scopeTwoMethod]);

  // KPI-card "explain this number" drawer (Total / Top Share / Drivers / Biggest Mover).
  const [selectedMetric, setSelectedMetric] = React.useState<HotspotMetric | null>(null);
  React.useEffect(() => {
    setSelectedMetric(null);
  }, [matrixDimension, selectedReportPeriodId, scopeTwoMethod, comparisonReportPeriod?.id]);

  // PDF of the view on screen: same dimension, basis, comparison and table length. The resolved
  // comparison period is sent (never "automatic"), so the PDF compares against what the screen shows.
  const [downloadingPdf, setDownloadingPdf] = React.useState<boolean>(false);
  const handleDownloadPdf = () => {
    if (!selectedReportPeriodId || !data || downloadingPdf) return;
    setDownloadingPdf(true);
    const friendlyName = data.reportPeriod?.friendlyName || selectedReportPeriod?.friendlyName || '';
    analyticsService
      .getHotspotsPdf(selectedReportPeriodId, {
        dim: matrixDimension,
        basis: scopeTwoMethod,
        compare: comparisonReportPeriod?.id,
        rows: showAllRanked ? 'all' : 'top',
      })
      .then((blob) => downloadBlob({ blob, fileName: `${`Emissions Hotspot Analysis ${friendlyName}`.trim()}.pdf` }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'The PDF could not be prepared, please try again', backgroundColor: 'text.primary' }))
      .finally(() => setDownloadingPdf(false));
  };

  return (
    <PageOuterContainer>
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'clip' }}>
        <PageHeading
          title='Emissions Hotspot Analysis'
          menuActions={[
            {
              label: downloadingPdf ? 'Preparing PDF…' : 'Download PDF',
              icon: <PictureAsPdfOutlined />,
              onClick: handleDownloadPdf,
              disabled: !data || isLoading || downloadingPdf,
            },
          ]}
        />

        <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'clip' }}>
        {/* Controls. The report period is governed by the top-bar selector. */}
        {allReportPeriods.length > 0 && (
          <Box
            sx={{
              mt: 0.5,
              mb: 2,
              display: 'flex',
              flexDirection: isMobileMode ? 'column' : 'row',
              flexWrap: 'wrap',
              alignItems: isMobileMode ? 'stretch' : 'center',
              justifyContent: isMobileMode ? 'flex-start' : 'flex-end',
              gap: 1.5,
            }}
          >
            {/* Hidden by the component itself when the org has fewer than two periods. */}
            <ComparisonPeriodSelect
              value={compareReportPeriodId}
              onChange={setCompareReportPeriodId}
              reportPeriods={allReportPeriods}
              selectedReportPeriodId={selectedReportPeriodId}
              autoComparisonPeriod={autoComparisonReportPeriod}
              fullWidth={isMobileMode}
            />
            <EmissionBasisToggle
              value={selectedEmissionType}
              onChange={setSelectedEmissionType}
              orientation={isMobileMode ? 'vertical' : 'horizontal'}
              fullWidth={isMobileMode}
              buttonSx={{ pt: 0, pb: 0, width: !isMobileMode ? '180px' : '100%', height: 40 }}
              sx={{ width: isMobileMode ? '100%' : 'auto' }}
            />
          </Box>
        )}

        {/* States */}
        {!selectedReportPeriodId && reportPeriodContext.reportPeriodsLoaded && (
          <Box sx={{ width: '100%', p: 4 }}>
            <Typography color='text.secondary'>
              {allReportPeriods.length === 0
                ? 'Create a reporting period to see hotspots.'
                : 'Select a report period at the top of the page to view its emissions hotspots.'}
            </Typography>
          </Box>
        )}
        {selectedReportPeriodId && isLoading && (
          <Fade in={showSkeleton} unmountOnExit>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
              <LoadingSpinner />
            </Box>
          </Fade>
        )}
        {selectedReportPeriodId && !isLoading && errorMessage && (
          <Box sx={{ width: '100%', p: 2 }}>
            <Typography color='error'>{errorMessage}</Typography>
          </Box>
        )}

        {selectedReportPeriodId && !isLoading && !errorMessage && data && (
          <Box sx={{ width: '100%' }}>
            {data.totals.total <= 0 ? (
              <Box sx={{ p: 4 }}>
                <Typography color='text.secondary'>No emissions recorded for this report period.</Typography>
              </Box>
            ) : (
              <HotspotAnalysisBody
                data={data}
                matrixDimension={matrixDimension}
                dimensionLabel={dimensionLabel}
                showAllRanked={showAllRanked}
                onToggleShowAll={() => setShowAllRanked((current) => !current)}
                onSelectMetric={setSelectedMetric}
                onDrill={openDrill}
                onDimensionChange={setMatrixDimension}
              />
            )}
          </Box>
        )}
        </Box>
      </Box>

      <DrillDownDrawer
        open={!!selectedHotspot}
        onClose={() => setSelectedHotspot(null)}
        selection={selectedHotspot}
        reportPeriodId={selectedReportPeriodId}
        periodLabel={data?.reportPeriod?.friendlyName || selectedReportPeriod?.friendlyName}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonReportPeriod?.id}
        dimensionLabel={dimensionLabel(selectedHotspot?.dimension || matrixDimension)}
      />

      <HotspotMetricDrawer
        open={!!selectedMetric}
        onClose={() => setSelectedMetric(null)}
        metric={selectedMetric}
        data={data}
        dimensionKey={matrixDimension}
        dimensionLabel={dimensionLabel(matrixDimension)}
        defaultDimensionLabel={dimensionLabel(data?.defaultDimension || matrixDimension)}
        prevTotal={prevTotal}
        comparisonLabel={prevLabel}
      />
    </PageOuterContainer>
  );
};
